import { DeviceUnitRecord } from 'core/deviceManagement/DeviceManagement';

/** 這一行為什麼沒被納入名單，或者納入了但對不起來。 */
export enum DeviceImportIssue {
  /** 兩段的行，分不出哪一段是店號。 */
  AMBIGUOUS = 'ambiguous',
  /** 段數對得上，但該是店號的那一段不是數字。 */
  INVALID_STORE_NUM = 'invalidStoreNum',
  /** 店號有查到，但輸入的縣市或店名與查到的那一筆不一樣。 */
  MISMATCH = 'mismatch',
  /** 店號解得出來，但這個渠道底下沒有這一間。 */
  NOT_FOUND = 'notFound'
}

export const DEVICE_IMPORT_ISSUE_I18N: Record<DeviceImportIssue, string> = {
  [DeviceImportIssue.AMBIGUOUS]: 'deviceManagement.messages.importAmbiguous',
  [DeviceImportIssue.INVALID_STORE_NUM]:
    'deviceManagement.messages.importInvalidStoreNum',
  [DeviceImportIssue.MISMATCH]: 'deviceManagement.messages.importMismatch',
  [DeviceImportIssue.NOT_FOUND]: 'deviceManagement.messages.importNotFound'
};

export type DeviceImportEntry = {
  /** 原始那一行，需確認清單要照原樣顯示出來讓人比對。 */
  raw: string;
  /** 比對鍵。只有這個欄位會送給後端。 */
  storeNum?: string;
  /** 只作校驗，不參與比對。 */
  municipality?: string;
  /** 只作校驗，不參與比對。 */
  unitName?: string;
  issue?: DeviceImportIssue;
};

export type DeviceImportList = {
  fileName: string;
  /** 每一行的解析結果，含解不出來的。「共 N 筆」數的就是這個。 */
  entries: DeviceImportEntry[];
  /** 去重後要送給後端的店號。 */
  storeNums: string[];
};

/** 範例檔與貼上區用的分隔符號：底線是主要格式，CSV 與 Tab 一併吃下。 */
const SEGMENT_SEPARATOR = /[_,\t]/;

/**
 * 只會出現在欄位標題、不會出現在資料裡的字眼。
 *
 * 「門市」刻意不在裡面：它是店名很常見的字（「天福門市」），列進去會讓貼上的
 * 第一筆被當成標題吃掉。
 */
const HEADER_KEYWORDS = ['縣市', '店號', '店名', '經度', '緯度'];

const isStoreNum = (value: string) => /^\d+$/.test(value);

/**
 * 一行拆成一筆。
 *
 * 只有段數決定哪一段是店號，不用「看起來像數字的那一段」去猜：店名與縣市都可能
 * 是純數字（例如「7」開頭的門市代號），猜錯的那一筆會安靜地比對到別間店。
 *
 * 一段是裸店號；三段是 `縣市_店號_店名`；四段是機台版，店號一樣在第二段。
 * 兩段分不出是「縣市_店號」還是「店號_店名」，所以報為無效而不是賭一邊。
 */
export function parseDeviceImportLine(raw: string): DeviceImportEntry {
  const line = raw.trim().replace(/^"|"$/g, '');
  const segments = line
    .split(SEGMENT_SEPARATOR)
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '');

  if (segments.length === 1) {
    return isStoreNum(segments[0])
      ? { raw: line, storeNum: segments[0] }
      : { raw: line, issue: DeviceImportIssue.INVALID_STORE_NUM };
  }
  if (segments.length === 2) {
    return { raw: line, issue: DeviceImportIssue.AMBIGUOUS };
  }
  if (segments.length >= 3) {
    const storeNum = segments[1];
    if (!isStoreNum(storeNum)) {
      return { raw: line, issue: DeviceImportIssue.INVALID_STORE_NUM };
    }
    return {
      raw: line,
      storeNum,
      municipality: segments[0],
      unitName: segments[2]
    };
  }
  return { raw: line, issue: DeviceImportIssue.INVALID_STORE_NUM };
}

/**
 * 這一行是不是欄位標題。
 *
 * 兩個條件要同時成立：解析不出店號，而且行內出現了只有標題才有的字眼。
 * 兩個都必要 —— 只看「是第一行」會把貼上的第一筆砍掉（貼上的內容沒有標題列，
 * 第一行就是資料）；只看「解析不出店號」則會把打錯的第一筆當標題吃掉，
 * 那一筆從此不會出現在需確認清單裡，使用者沒有任何線索。
 */
export function isDeviceImportHeaderLine(raw: string): boolean {
  const entry = parseDeviceImportLine(raw);
  if (entry.storeNum !== undefined) {
    return false;
  }
  return HEADER_KEYWORDS.some((keyword) => entry.raw.includes(keyword));
}

/**
 * 整段文字拆成多筆。空行不算一筆，第一行是欄位標題時也不算。
 *
 * 只檢查第一行：標題只會在檔案開頭出現一次。中間那些解不出來的行照樣留著，
 * 它們會以需確認的形式被列出來，而不是安靜地消失。
 */
export function parseDeviceImportText(text: string): DeviceImportEntry[] {
  const lines = String(text ?? '')
    .split(/[\r\n]+/)
    .map((line) => line.trim())
    .filter((line) => line !== '');
  const body =
    lines.length > 0 && isDeviceImportHeaderLine(lines[0])
      ? lines.slice(1)
      : lines;
  return body.map(parseDeviceImportLine);
}

/** 解析完的結果；一筆有效店號都沒有就回 undefined，名單模式不會開起來。 */
export function buildDeviceImportList(
  fileName: string,
  text: string
): DeviceImportList | undefined {
  const entries = parseDeviceImportText(text);
  const storeNums = entries.reduce<string[]>((acc, entry) => {
    if (entry.storeNum && !acc.includes(entry.storeNum)) {
      acc.push(entry.storeNum);
    }
    return acc;
  }, []);
  if (storeNums.length === 0) {
    return undefined;
  }
  return { fileName, entries, storeNums };
}

/**
 * 輸入的縣市與店名和查回來的那一筆對不對得上。
 *
 * 只提示不阻擋：比對鍵是店號，縣市與店名是人維護的欄位，兩邊寫法不同
 * （「台北市」與「臺北市」、店名有沒有帶「門市」）很常見，拿它擋掉整筆
 * 會把對的名單擋在外面。
 *
 * `records` 要是整份名單比對回來的那一批，與 `findDeviceImportMisses` 用的是同一批。
 * 給清單當下那一頁的話，同一份名單在第 1 頁與第 2 頁會算出兩個不同的需確認數，
 * 而那個數字讀起來像是對整份名單下的結論。比對不完整時呼叫端給空陣列，這裡就一筆
 * 都不報。
 */
export function findDeviceImportMismatches(
  entries: DeviceImportEntry[],
  records: DeviceUnitRecord[]
): DeviceImportEntry[] {
  const byStoreNum = new Map<string, DeviceUnitRecord>();
  records.forEach((record) => {
    if (!byStoreNum.has(record.storeNum)) {
      byStoreNum.set(record.storeNum, record);
    }
  });
  return entries.filter((entry) => {
    if (!entry.storeNum) {
      return false;
    }
    const record = byStoreNum.get(entry.storeNum);
    if (!record) {
      return false;
    }
    const nameMismatch =
      !!entry.unitName && !record.unitName.includes(entry.unitName);
    const municipalityMismatch =
      !!entry.municipality &&
      !(record.address ?? '').includes(
        entry.municipality.replace('台', '臺')
      ) &&
      !(record.address ?? '').includes(entry.municipality.replace('臺', '台'));
    return nameMismatch || municipalityMismatch;
  });
}

/**
 * 名單裡哪幾筆的店號在這個渠道底下查不到。
 *
 * `matchedStoreNums` 必須是「整份名單比對過的結果」，不可以拿清單當下那一頁湊數：
 * 「查不到」是一句否定的斷言，而分頁回來的那二十列只證明得了「這幾筆查得到」。
 * 拿一頁去推的話，第二頁之後的每一筆都會被冠上「查無此店號」。
 * 比對不完整時呼叫端給 undefined，這裡就一筆都不報 —— 不知道的時候不要亂講。
 */
export function findDeviceImportMisses(
  entries: DeviceImportEntry[],
  matchedStoreNums?: string[]
): DeviceImportEntry[] {
  if (matchedStoreNums === undefined) {
    return [];
  }
  const matched = new Set(matchedStoreNums);
  return entries.filter(
    (entry) => entry.storeNum !== undefined && !matched.has(entry.storeNum)
  );
}

/** 範例檔的內容。第一行是表頭，解析時會被跳過。 */
export const DEVICE_IMPORT_TEMPLATE_ROWS: string[][] = [
  ['縣市_店號_店名'],
  ['臺北市_177463_天福'],
  ['新北市_273684_江東']
];

/** 「載入示範名單」貼進輸入框的內容。 */
export const DEVICE_IMPORT_SAMPLE_TEXT = [
  '臺北市_177463_天福',
  '臺北市_234232_宏圖',
  '新北市_273684_江東',
  '128335'
].join('\n');
